import { isFilledByUs } from "./noFieldPick";
import { copySource } from "./copySource";
import {
  NEW_POOL_WARNING,
  LEAF_NEW,
  REASON_ANSWERED_BEFORE,
  REASON_NO_MAJORITY,
  REASON_PRE_FILLED,
  REASON_REPEATED_CONCEPT,
  REASON_UNIT_MISSING,
  REASON_AGREED,
} from "./reviewCodes";
import { pickConfidence, valuesToCheck } from "./reviewGroups";
import { asReviewRow, type RowInput } from "./reviewRow.types";
import { isApprovedAttributeReviewStatus } from "./reviewQueueStatus";

export type ConfidenceView = {
  text: string;
  short: string;
  tone: "good" | "plain" | "amber" | "none";
  title: string;
};

export type WorkChipView = {
  text: string;
  tone: "amber" | "quiet" | "draft" | "system";
};

export type WorkChipContext = {
  sameLeafLabel?: string;
  isDraft: boolean;
  isQueued: boolean;
  notSaved?: boolean;
};

const percent = (p: number) => `${Math.round(p * 100)}%`;

export function confidenceView(input: RowInput): ConfidenceView {
  const row = asReviewRow(input);
  const detail = row.reviewDetail ? String(row.reviewDetail) : "";
  const p = pickConfidence(row);
  if (!row.leaf) {
    return { text: "—", short: "—", tone: "none", title: "No leaf picked yet" };
  }
  if (row.reviewReason === REASON_PRE_FILLED) {
    const text = `copied · ${copySource(detail).store ?? "older answer"}`;
    return { text, short: text, tone: "plain", title: detail || text };
  }
  if (row.reviewReason === REASON_ANSWERED_BEFORE) {
    return {
      text: "used before",
      short: "used before",
      tone: "plain",
      title: detail || "This field was answered in an earlier session",
    };
  }
  if (p === null) {
    return {
      text: "—",
      short: "—",
      tone: "none",
      title: "No model confidence yet",
    };
  }
  if (row.reviewReason === REASON_AGREED) {
    return {
      text: `${percent(p)} · both AIs agree`,
      short: percent(p),
      tone: "good",
      title:
        detail ||
        "How sure the two models are (the lower of the two). Both picked the same kind.",
    };
  }
  return {
    text: percent(p),
    short: percent(p),
    tone: "plain",
    title: detail || "How sure the two models are (the lower of the two)",
  };
}

export function workChips(
  input: RowInput,
  ctx: WorkChipContext,
): WorkChipView[] {
  const row = asReviewRow(input);
  const approved = isApprovedAttributeReviewStatus(row.decisionStatus);
  const toCheck = approved ? 0 : valuesToCheck(row);
  const chips: (WorkChipView | false | "" | null | undefined)[] = [
    isFilledByUs(row.kind) && {
      text: "System field, server fills it",
      tone: "system",
    },
    ctx.notSaved && { text: "Not saved", tone: "amber" },
    toCheck > 0 && {
      text: `${toCheck} value${toCheck === 1 ? "" : "s"} to check`,
      tone: "amber",
    },
    row.leafSource === LEAF_NEW && { text: "new field", tone: "quiet" },
    row.reviewReason === REASON_REPEATED_CONCEPT && {
      text: "same meaning as another field",
      tone: "quiet",
    },
    row.reviewReason === REASON_UNIT_MISSING && {
      text: "needs a unit",
      tone: "amber",
    },
    row.reviewReason === REASON_NO_MAJORITY && {
      text: "AIs unsure",
      tone: "amber",
    },
    ctx.sameLeafLabel && {
      text: `same leaf as ${ctx.sameLeafLabel}`,
      tone: "amber",
    },
    ...(row.warnings ?? [])
      .filter((warning) => warning !== NEW_POOL_WARNING)
      .map((warning): WorkChipView => ({ text: warning, tone: "amber" })),
    ctx.isDraft && { text: "draft", tone: "draft" },
    ctx.isQueued && { text: "saving…", tone: "quiet" },
    approved &&
      row.reviewReason === REASON_ANSWERED_BEFORE && {
        text: "answered before",
        tone: "quiet",
      },
  ];
  return chips.filter((chip): chip is WorkChipView => Boolean(chip));
}
